/** A-13 — pharmacy settings singleton editor. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { getPharmacySettings, patchPharmacySettings } from "../../api/admin";
import type { PharmacySettings as Settings } from "../../api/admin";
import { describeError } from "../../api/errors";
import { StateSelect } from "../../components/ui/StateSelect";
import { isValidLocal, localDigits, typedLocal } from "../../lib/phone";
import { isValidPinCode, pinDigits } from "../../lib/pincode";

// A business name as registered (ADM-07): letters, digits, spaces and . & - ( ) ' / , — so
// "S.K. Medicals (P) Ltd" is accepted; only letters, spaces and dots used to be, which blocked
// every save. A city: letters, spaces, dots, hyphens and apostrophes.
const BUSINESS_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9 .&()'/,-]*$/;
const sanitizeBusinessName = (v: string) => v.replace(/[^A-Za-z0-9 .&()'/,-]/g, "").replace(/^[^A-Za-z0-9]+/, "");
const CITY_RE = /^[A-Za-z][A-Za-z .'-]*$/;
const sanitizeCity = (v: string) => v.replace(/[^A-Za-z .'-]/g, "").replace(/^[^A-Za-z]+/, "");
// Delivery GST: 0-28%, at most 2 decimals (the API's range, ADM-08).
const GST_RE = /^\d{1,2}(\.\d{1,2})?$/;

// GSTIN: 2-digit state code + 10-char PAN + entity number (1-9/A-Z) + "Z" + checksum,
// e.g. 33ABCDE1234F1Z5 (Tamil Nadu, PAN ABCDE1234F, 1st registration, Z, checksum 5).
const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

// local@domain.tld — no spaces, one "@", and a TLD of at least 2 letters.
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

interface Field {
  key: keyof Settings;
  label: string;
  /** Strips disallowed characters as the user types. */
  sanitize?: (v: string) => string;
  /** Error message for a non-empty value that doesn't match the field's format. */
  validate?: (v: string) => string | null;
  type?: string;
  inputMode?: "text" | "numeric" | "decimal" | "email";
  maxLength?: number;
  placeholder?: string;
  /** May be left blank (sent as null). */
  optional?: boolean;
}

const nameField = (key: keyof Settings, label: string): Field => ({
  key,
  label,
  sanitize: sanitizeBusinessName,
  validate: (v) =>
    BUSINESS_NAME_RE.test(v) ? null : `${label} can contain letters, digits, spaces and . & - ( ) ' / ,`,
});

const FIELDS: Field[] = [
  nameField("name", "Name"),
  nameField("legal_name", "Legal name"),
  { key: "address_line1", label: "Address line 1" },
  // Nullable in the database and NULL in the seed: a required field here meant nothing saved (ADM-07).
  { key: "address_line2", label: "Address line 2", optional: true },
  {
    key: "city",
    label: "City",
    sanitize: sanitizeCity,
    validate: (v) => (CITY_RE.test(v) ? null : "City can contain letters, spaces and . - '"),
  },
  // A picker of the API's state codes (ACCT-07): GST compares it with each delivery address.
  { key: "state_code", label: "State" },
  {
    key: "pin_code",
    label: "PIN code",
    sanitize: pinDigits,
    validate: (v) =>
      v.length !== 6 ? "PIN code must be exactly 6 digits" : isValidPinCode(v) ? null : "PIN code cannot start with 0",
    inputMode: "numeric",
    maxLength: 6,
    placeholder: "600001",
  },
  {
    key: "gstin",
    label: "GSTIN",
    sanitize: (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15),
    validate: (v) => (GSTIN_RE.test(v) ? null : "Enter a valid 15-character GSTIN (e.g. 33ABCDE1234F1Z5)"),
    maxLength: 15,
    placeholder: "33ABCDE1234F1Z5",
  },
  {
    key: "phone",
    label: "Phone",
    sanitize: typedLocal, // a pasted "+91 98765 43210" is 9876543210 (AUTH-16)
    validate: (v) =>
      v.length !== 10
        ? "Phone must be exactly 10 digits"
        : isValidLocal(v)
          ? null
          : "Phone must start with 6, 7, 8 or 9",
    type: "tel",
    inputMode: "numeric",
    maxLength: 20,
    placeholder: "9876543210",
  },
  {
    key: "email",
    label: "Email",
    sanitize: (v) => v.replace(/\s/g, ""),
    validate: (v) => (EMAIL_RE.test(v) ? null : "Enter a valid email address (e.g. name@example.com)"),
    type: "email",
    inputMode: "email",
    placeholder: "name@example.com",
  },
  {
    key: "default_delivery_gst_pct",
    label: "Delivery GST %",
    sanitize: (v) => v.replace(/[^0-9.]/g, ""),
    validate: (v) =>
      GST_RE.test(v) && Number(v) <= 28 ? null : "Delivery GST must be 0 to 28, with at most 2 decimals",
    inputMode: "decimal",
    placeholder: "18",
  },
  { key: "pharmacy_license_number", label: "Pharmacy license #" },
];

// Every field but the optional ones is mandatory; format rules apply once something is entered.
function fieldError(f: Field, form: Partial<Settings>): string | null {
  const v = String(form[f.key] ?? "").trim();
  if (!v) return f.optional ? null : `${f.label} is required`;
  return f.validate?.(v) ?? null;
}

export function PharmacySettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["pharmacy-settings"], queryFn: getPharmacySettings });
  const [form, setForm] = useState<Partial<Settings>>({});
  // Errors show only after a field is left or submit is attempted, not while typing.
  const [touched, setTouched] = useState<Partial<Record<keyof Settings, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Phone is edited as 10 bare digits (drop any stored "+91" / separators); state code is upper-case.
    if (data)
      setForm({
        ...data,
        phone: localDigits(data.phone ?? ""),
        state_code: (data.state_code ?? "").toUpperCase(),
      });
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      const trimmed = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]),
      ) as Partial<Settings>;
      return patchPharmacySettings({
        ...trimmed,
        // Blank clears the second line; the phone goes in the +91 form every other phone uses (ADM-08).
        address_line2: trimmed.address_line2 || null,
        phone: trimmed.phone ? `+91${trimmed.phone}` : trimmed.phone,
      });
    },
    onSuccess: () => {
      toast.success("Pharmacy settings saved successfully.");
      qc.invalidateQueries({ queryKey: ["pharmacy-settings"] });
    },
    onError: (e) => toast.error(describeError(e)),
  });

  return (
    <div className="max-w-xl space-y-4">
      <h2 className="text-lg font-semibold">Pharmacy settings</h2>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(true);
          const firstInvalid = FIELDS.find((f) => fieldError(f, form));
          if (firstInvalid) {
            (e.currentTarget.elements.namedItem(firstInvalid.key) as HTMLInputElement | null)?.focus();
            return;
          }
          save.mutate();
        }}
        className="space-y-3 rounded-lg border bg-white p-5"
      >
        {/* Fields are locked while the save request is in flight. */}
        <fieldset disabled={save.isPending} className="space-y-3">
          {FIELDS.map((f) => {
            const error = submitted || touched[f.key] ? fieldError(f, form) : null;
            return (
              <label key={f.key} className="flex flex-col gap-1 text-sm">
                <span className="text-gray-500">
                  {f.label}
                  {f.optional ? (
                    <span className="ml-1 text-xs text-gray-400">(optional)</span>
                  ) : (
                    <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>
                  )}
                </span>
                {f.key === "state_code" ? (
                  <StateSelect
                    name={f.key}
                    value={(form.state_code as string) ?? ""}
                    onChange={(code) => setForm({ ...form, state_code: code })}
                    onBlur={() => setTouched({ ...touched, [f.key]: true })}
                    invalid={!!error}
                    className={`rounded-sm ${error ? "border-red-500" : ""}`}
                  />
                ) : (
                  <input
                    name={f.key}
                    type={f.type ?? "text"}
                    inputMode={f.inputMode}
                    maxLength={f.maxLength}
                    placeholder={f.placeholder}
                    value={(form[f.key] as string) ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, [f.key]: f.sanitize ? f.sanitize(e.target.value) : e.target.value })
                    }
                    onBlur={() => setTouched({ ...touched, [f.key]: true })}
                    required={!f.optional}
                    aria-invalid={!!error}
                    className={`rounded-sm border px-3 py-1.5 disabled:bg-gray-50 disabled:text-gray-500 ${
                      error ? "border-red-500" : ""
                    }`}
                  />
                )}
                {error && <span className="text-xs text-red-600">{error}</span>}
              </label>
            );
          })}
        </fieldset>
        <div className="flex items-center gap-3 pt-2">
          <button
            className="inline-flex items-center gap-2 rounded-sm bg-brand px-4 py-1.5 text-brand-fg disabled:cursor-not-allowed disabled:opacity-60"
            disabled={save.isPending}
            aria-busy={save.isPending}
          >
            {save.isPending && (
              <span
                className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                aria-hidden="true"
              />
            )}
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
